import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { callPublic } from '../lib/api';
import { addLine, cartCount, loadCart, removeLine, saveCart, setQuantity } from '../lib/cart';
import type { CartLine, PublicStore } from '../lib/types';
import { useApp } from './AppContext';

interface StoreState {
  slug: string;
  store: PublicStore | null;
  loading: boolean;
  error: string | null;
  reloadStore: () => Promise<void>;
  cart: CartLine[];
  cartCount: number;
  addToCart: (item: string, quantity?: number, notes?: string) => void;
  setCartQuantity: (item: string, quantity: number, notes?: string) => void;
  removeFromCart: (item: string, notes?: string) => void;
  clearCart: () => void;
  /** Signed-in user is bound to THIS store (customer or owner). */
  boundHere: boolean;
  /** Signed-in user is bound to a different store. */
  boundElsewhere: boolean;
}

const Ctx = createContext<StoreState | null>(null);
export function useStore(): StoreState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStore must be used within StoreProvider');
  return v;
}

export function StoreProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const { me, authed, ensureSession } = useApp();
  const [store, setStore] = useState<PublicStore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartLine[]>(() => loadCart(slug));

  const reloadStore = useCallback(async () => {
    setLoading(true);
    try {
      const r = await callPublic<{ store: PublicStore }>('get_store', { slug });
      setStore(r.store); setError(null);
    } catch (e) {
      setStore(null);
      setError(e instanceof Error ? e.message : 'Could not load this store.');
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { void reloadStore(); }, [reloadStore]);
  useEffect(() => { setCart(loadCart(slug)); }, [slug]);
  useEffect(() => { saveCart(slug, cart); }, [slug, cart]);
  // Guest checkout: the first thing in the cart starts a (guest) session so
  // the cart can be priced and ordered without an account.
  useEffect(() => { if (cart.length > 0 && !authed) void ensureSession(); }, [cart.length, authed, ensureSession]);

  // Store colours drive the look of every page under /:slug.
  useEffect(() => {
    const root = document.documentElement;
    const c = store?.branding.colors;
    root.style.setProperty('--primary', c?.primary || '#1976d2');
    root.style.setProperty('--secondary', c?.secondary || '#424242');
    document.title = store ? store.displayName : 'StoreFront';
    return () => {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--secondary');
      document.title = 'StoreFront';
    };
  }, [store]);

  const value = useMemo<StoreState>(() => ({
    slug, store, loading, error, reloadStore,
    cart, cartCount: cartCount(cart),
    addToCart: (item, quantity = 1, notes) => setCart((c) => addLine(c, item, quantity, notes)),
    setCartQuantity: (item, quantity, notes) => setCart((c) => setQuantity(c, item, quantity, notes)),
    removeFromCart: (item, notes) => setCart((c) => removeLine(c, item, notes)),
    clearCart: () => setCart([]),
    boundHere: !!me && me.businessSlug === slug,
    boundElsewhere: !!me && me.businessSlug !== slug,
  }), [slug, store, loading, error, reloadStore, cart, me]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
